import { faBullhorn, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { React, moment } from 'Imports';
import ActionButton from './ActionButton';
import DetailsField from './DetailsField';
import { VideoEventDescription } from '$Components/Shared/VideoEventDescription';
import { isEncompass } from '$Utilities/integrationPlatform';
import * as DateFormatter from '$Components/Shared/DateFormatter';
import { getStatusLabel } from '$Utilities/videoEventStatusUtility';
import { VideoEventResponse } from '$Generated/api';
import { useEffect, useState } from 'react';
import { canEditByRole, RolesEnum } from '$Externals/VerifyRole';
import PromoteRecallDialog from '$Components/VideoRecall/PromoteRecallDialog';
import DeleteRecallDialog from '$Components/VideoRecall/DeleteRecallDialog';
import { IVideoEventServiceInjectedProps, VideoEventService } from '$State/VideoEventFreezerService';

interface VideoRecallDetailsSectionProps {
    videoEvent: VideoEventResponse;
    onPromoted: (eventId: number) => void;
    onDeleted: (eventId: number) => void;
}

const styles = require('./DetailsSection.scss') as {
    flagAndTitleSection: string;
    dataSectionGeotab: string;
    dataSectionEncompass: string;
    actionsSection: string;
};

const _VideoRecallDetailsSection = (props: VideoRecallDetailsSectionProps & IVideoEventServiceInjectedProps) => {
    const [userCanDelete, setUserCanDelete] = useState(false);
    const [isPromoteDialogOpen, setIsPromoteDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    // Falls back to the (possibly stale) prop only until the freezer has its own copy -- the parent page
    // only pushes a fresh videoEvent prop on a full reload, but updateLocalVideoEventStatus (e.g. after
    // playing a recall video) updates just the freezer, so the live value must be read from there too.
    const liveStatus = props.videoEvents.getState().selectedVideoEventResult.data?.videoEventStatus ?? props.videoEvent.videoEventStatus;
    const eventStatusLabel = getStatusLabel(liveStatus!);
    const eventDate = props.videoEvent.eventStartDate ? moment(props.videoEvent.eventStartDate) : moment();
    const requestedOn = moment(props.videoEvent.requestedOn ?? undefined);
    const completedOn = moment(props.videoEvent.cameraRequest?.completionDate ?? undefined);

    useEffect(() => {
        setUserCanDelete(canEditByRole([RolesEnum.videoAdmin, RolesEnum.videoSuperAdmin]));
    }, []);

    const handlePromoteClick = () => {
        setIsPromoteDialogOpen(true);
    };

    const handleOnPromoted = (eventId: number) => {
        props.onPromoted(eventId);
        setIsPromoteDialogOpen(false);
    };

    const handleDeleteClick = () => {
        setIsDeleteDialogOpen(true);
    };

    const handleOnDeleted = (eventId: number) => {
        props.onDeleted(eventId);
        setIsDeleteDialogOpen(false);
    };

    return (
        <>
            <div className={styles.flagAndTitleSection}>
                <div>
                    <VideoEventDescription
                        videoEventTypePairings={props.videoEvent.videoEventTypePairings}
                        videoEventTypeString={props.videoEvent.videoEventTypeString}
                        videoEventFontSize="18px"
                        videoEventFontWeight={isEncompass ? '700' : '500'}
                    />
                </div>
            </div>
            <div className={isEncompass ? styles.dataSectionEncompass : styles.dataSectionGeotab}>
                <DetailsField label="Event Status">{eventStatusLabel}</DetailsField>

                <DetailsField label="Event ID">{props.videoEvent.displayId}</DetailsField>

                <DetailsField label="Date/Time">
                    {DateFormatter.date(eventDate)}
                    <br />
                    {DateFormatter.timeWithSecondsAndTimezone(eventDate)}
                </DetailsField>

                <DetailsField label="Vehicle">{props.videoEvent.vehicleDescription || 'N/A'}</DetailsField>

                <DetailsField label="Camera Serial #">{props.videoEvent.cameraDeviceSerialNumber}</DetailsField>

                <DetailsField label="Duration">{props.videoEvent.eventDurationSeconds}s</DetailsField>

                <DetailsField label="Requested By">{props.videoEvent.requestedBy}</DetailsField>

                <DetailsField label="Requested On">
                    {DateFormatter.date(requestedOn)}
                    <br />
                    {DateFormatter.timeWithSecondsAndTimezone(requestedOn)}
                </DetailsField>

                <DetailsField label="Completed On">
                    {DateFormatter.date(completedOn)}
                    <br />
                    {DateFormatter.timeWithSecondsAndTimezone(completedOn)}
                </DetailsField>
            </div>
            <div className={styles.actionsSection}>
                <ActionButton icon={faBullhorn} text="Promote" onClick={handlePromoteClick} />
                <ActionButton icon={faTrashAlt} text="Delete" onClick={handleDeleteClick} disabled={!userCanDelete} />
            </div>
            <PromoteRecallDialog
                videoEventId={props.videoEvent.id!}
                open={isPromoteDialogOpen}
                onPromote={handleOnPromoted}
                onClose={() => setIsPromoteDialogOpen(false)}
            />
            <DeleteRecallDialog
                videoEventId={props.videoEvent.id!}
                open={isDeleteDialogOpen}
                onDelete={handleOnDeleted}
                onClose={() => setIsDeleteDialogOpen(false)}
            />
        </>
    );
};

const VideoRecallDetailsSection = VideoEventService.inject(_VideoRecallDetailsSection);

export default VideoRecallDetailsSection;
